import React, { useState } from 'react';
import {
  Alert, Box, CircularProgress, Dialog, DialogContent, DialogTitle,
  IconButton, Paper, Stack, Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  InboxOutlined as EmptyIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../redux/actions';
import DayExpensesModal from '../dialogs/DayExpensesModal';
import UnverifiedDatesTable from '../tables/UnverifiedDatesTable';

const formatDate = (value) => {
  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('th-TH', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

// การ์ดนี้เป็น presentational ล้วน — การ fetch รายการวัน, state ของแท็บ และแถบแท็บ ย้ายไปอยู่ที่
// OcrDailyVerification.js (parent) แล้ว เพราะผู้ใช้ต้องการให้แท็บลอยอยู่นอกการ์ด บนสุดของหน้า
// การ์ดรับ rows ที่กรองตามแท็บมาแล้ว + สถานะ loading/error/isEmpty + onReload (เรียกหลังยืนยัน OCR)
export function UnverifiedExpenseDatesCard({
  customerId, companyOpeningBalance, companyOpeningBalanceDate, rows, loading, error, isEmpty, emptyLabel,
  onReload, tab,
}) {
  // แท็บ "สำเร็จแล้ว" -> modal โชว์เฉพาะรายการที่ยืนยันแล้ว, "รอยืนยันอีกครั้ง" -> เฉพาะที่ค้าง
  const modalStatusFilter = tab === 'verified' ? 'verified' : tab === 'external' ? 'unverified' : 'all';
  const dispatch = useDispatch();
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewImagesDate, setViewImagesDate] = useState(null);
  const [viewImages, setViewImages] = useState([]);
  const [viewImagesLoading, setViewImagesLoading] = useState(false);
  const [viewImagesError, setViewImagesError] = useState('');

  // เปิดดูรูปที่เคยแนบตอนกด "ยืนยันรายการที่ตรงกัน" ของวันนั้น (ดึงจาก OcrVerificationLog
  // ผ่าน getOcrVerificationImagesForDate) — โหลดตามจังหวะที่กดเท่านั้น ไม่โหลดล่วงหน้าทุกแถว
  const openImages = async (date) => {
    setViewImagesDate(date);
    setViewImages([]);
    setViewImagesError('');
    setViewImagesLoading(true);
    try {
      const data = await dispatch(actions.getOcrVerificationImagesForDate({ customerId, date }));
      setViewImages(data.images || []);
    } catch (err) {
      setViewImagesError(err.message);
    } finally {
      setViewImagesLoading(false);
    }
  };

  const closeImages = () => setViewImagesDate(null);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', borderColor: '#e2e8f0' }}>
      {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
          <Stack spacing={1.5} alignItems="center"><CircularProgress size={30} /><Typography variant="body2" color="text.secondary">กำลังโหลดรายการ...</Typography></Stack>
        </Box>
      ) : isEmpty ? (
        <Box sx={{ py: 7, px: 2, textAlign: 'center', bgcolor: '#fafafa' }}>
          <EmptyIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
          <Typography sx={{ fontWeight: 700 }}>ไม่มีรายการค้างตรวจสอบ</Typography>
          <Typography variant="body2" color="text.secondary">รายการ OCR ทั้งหมดได้รับการตรวจสอบเรียบร้อยแล้ว</Typography>
        </Box>
      ) : (
        <Box sx={{ p: { xs: 1.25, md: 2 }, bgcolor: '#f8fafc' }}>
          <UnverifiedDatesTable
            rows={rows}
            onSelectDate={setSelectedDate}
            onViewImages={openImages}
            emptyLabel={emptyLabel}
          />
        </Box>
      )}

      <DayExpensesModal open={Boolean(selectedDate)} onClose={() => setSelectedDate(null)} customerId={customerId} date={selectedDate} companyOpeningBalance={companyOpeningBalance} companyOpeningBalanceDate={companyOpeningBalanceDate} onVerified={onReload} statusFilter={modalStatusFilter} />

      <Dialog open={Boolean(viewImagesDate)} onClose={closeImages} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            รูปที่ใช้ยืนยัน OCR — {viewImagesDate ? formatDate(viewImagesDate) : ''}
          </Typography>
          <IconButton size="small" onClick={closeImages}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewImagesLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
          )}
          {viewImagesError && <Alert severity="error">{viewImagesError}</Alert>}
          {!viewImagesLoading && !viewImagesError && viewImages.length === 0 && (
            <Typography variant="body2" color="text.secondary">ไม่พบรูปที่ใช้ยืนยัน OCR ของวันนี้</Typography>
          )}
          {viewImages.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={1.5}>
              {viewImages.map((img) => (
                <a key={img.id} href={img.url} target="_blank" rel="noreferrer">
                  <Box
                    component="img"
                    src={img.url}
                    alt="หลักฐานยืนยัน OCR"
                    sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 1.5, border: '1px solid #e2e8f0' }}
                  />
                </a>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}

export default UnverifiedExpenseDatesCard;
